import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'

// ==================== Query Keys ====================
const BADGES_KEY = 'gamification-badges'
const EMPLOYEE_XP_KEY = 'gamification-xp'
const XP_TRANSACTIONS_KEY = 'gamification-xp-transactions'
const LEADERBOARD_KEY = 'gamification-leaderboard'
const GAMIFICATION_SETTINGS_KEY = 'gamification-settings'
const MY_GAMIFICATION_KEY = 'my-gamification'

// ==================== Types ====================
export interface Badge {
  id: string
  name: string
  name_kk?: string | null
  description?: string | null
  description_kk?: string | null
  icon?: string | null
  color?: string | null
  category: string
  trigger_type: string
  trigger_config: Record<string, any>
  xp_reward: number
  is_repeatable: boolean
  sort_order: number
  is_active: boolean
  awarded_count?: number
  created_at: string
}

export interface EmployeeXpEntry {
  employee_id: string
  employee: {
    id: string
    first_name: string
    last_name: string
    middle_name?: string | null
    department?: {
      id: string
      name: string
      organization?: { id: string; name: string } | null
    } | null
    position?: { id: string; name: string } | null
  }
  total_xp: number
  level: number
  current_streak_days: number
  best_streak_days: number
  badges_count: number
}

export interface XpTransaction {
  id: string
  amount: number
  source: string
  description?: string | null
  work_date?: string | null
  created_at: string
}

export interface LeaderboardEntry {
  rank: number
  employee_id: string
  employee: {
    id: string
    first_name: string
    last_name: string
    middle_name?: string | null
    photo_url?: string | null
    department?: {
      id: string
      name: string
      organization?: { id: string; name: string } | null
    } | null
    position?: { id: string; name: string } | null
  }
  total_xp: number
  period_xp?: number
  level: number
  current_streak_days: number
  badges_count: number
}

export interface GamificationSettings {
  id: string
  is_enabled: boolean
  xp_on_time_checkin: number
  xp_on_time_checkout: number
  xp_no_late_day: number
  xp_test_passed: number
  xp_test_perfect: number
  xp_streak_multiplier: number
  level_thresholds: number[]
  leaderboard_visible_to_all: boolean
}

export interface BadgeFilters {
  search?: string
  category?: string
  trigger_type?: string
  is_active?: string
  page?: number
  limit?: number
  sort?: string
}

// ==================== Badges ====================

export const useBadges = (filters: BadgeFilters = {}) => {
  return useQuery({
    queryKey: [BADGES_KEY, filters],
    queryFn: async () => {
      const response = await apiClient.getBadges(filters)
      return { data: response.data || [], total: response.total || 0, page: response.page || 1, limit: response.limit || 20, totalPages: response.totalPages || 1 }
    },
    staleTime: 30_000,
  })
}

export const useCreateBadge = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Badge>) => apiClient.createBadge(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [BADGES_KEY] }) },
  })
}

export const useUpdateBadge = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Badge> }) => apiClient.updateBadge(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [BADGES_KEY] }) },
  })
}

export const useDeleteBadge = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteBadge(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [BADGES_KEY] }) },
  })
}

export const useAwardBadge = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ badgeId, employeeIds }: { badgeId: string; employeeIds: string[] }) =>
      apiClient.awardBadge(badgeId, { employee_ids: employeeIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [BADGES_KEY] })
      qc.invalidateQueries({ queryKey: [EMPLOYEE_XP_KEY] })
    },
  })
}

// ==================== Employee XP ====================

export const useEmployeeXpList = (params: Record<string, any> = {}) => {
  return useQuery({
    queryKey: [EMPLOYEE_XP_KEY, params],
    queryFn: async () => {
      const response = await apiClient.getEmployeeXpList(params)
      return { data: response.data || [], total: response.total || 0, page: response.page || 1, limit: response.limit || 20, totalPages: response.totalPages || 1 }
    },
    staleTime: 30_000,
  })
}

export const useXpTransactions = (employeeId: string, params: Record<string, any> = {}) => {
  return useQuery({
    queryKey: [XP_TRANSACTIONS_KEY, employeeId, params],
    queryFn: async () => {
      const response = await apiClient.getXpTransactions(employeeId, params)
      return { data: response.data || [], total: response.total || 0, page: response.page || 1, limit: response.limit || 20, totalPages: response.totalPages || 1 }
    },
    enabled: !!employeeId,
    staleTime: 30_000,
  })
}

export const useAwardXp = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { employee_id: string; amount: number; description?: string }) => apiClient.awardXp(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [EMPLOYEE_XP_KEY] })
      qc.invalidateQueries({ queryKey: [LEADERBOARD_KEY] })
    },
  })
}

// ==================== Leaderboard ====================

export const useLeaderboard = (params: { department_id?: string; organization_id?: string; limit?: number; period?: string } = {}) => {
  return useQuery({
    queryKey: [LEADERBOARD_KEY, params],
    queryFn: () => apiClient.getLeaderboard(params) as Promise<LeaderboardEntry[]>,
    staleTime: 30_000,
  })
}

// ==================== Settings ====================

export const useGamificationSettings = () => {
  return useQuery({
    queryKey: [GAMIFICATION_SETTINGS_KEY],
    queryFn: () => apiClient.getGamificationSettings() as Promise<GamificationSettings>,
    staleTime: 60_000,
  })
}

export const useUpdateGamificationSettings = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<GamificationSettings>) => apiClient.updateGamificationSettings(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [GAMIFICATION_SETTINGS_KEY] }) },
  })
}
